// チャット監視 (spec §4.6 モデレーター機能)。
// host_action が null のチャット (= 未対応) のみ表示。AI 候補生成 → 編集 → 投稿 / 無視。
//
// CLAUDE.md「AI 発信はホスト承認 UI 経由」: 候補生成は表示するだけ、ホストが投稿ボタンを押すまで
// Zoom Chat には流れない。

import {
  useCallback,
  useEffect,
  useState,
  type ReactElement,
} from 'react';
import { t, type Locale } from '@solo-webinar-copilot/i18n';
import type { ChatMessage } from '@solo-webinar-copilot/shared';
import {
  ignoreChatMessage,
  listChatMessages,
  postChatReply,
  suggestChatReply,
} from '../lib/api-client.ts';
import type { AppContext } from '../lib/zoom-sdk.ts';

const POLL_INTERVAL_MS = 3000;

export interface ChatMonitorProps {
  ctx: AppContext;
  locale: Locale;
}

export function ChatMonitor({ ctx, locale }: ChatMonitorProps): ReactElement {
  const [items, setItems] = useState<ChatMessage[]>([]);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    const r = await listChatMessages(ctx);
    if (r.ok) {
      // 未対応のものだけ表示
      setItems(r.items.filter((x) => !x.host_action));
      setError(null);
    } else {
      setError(r.error);
    }
  }, [ctx]);

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const tick = async () => {
      if (cancelled) return;
      await refresh();
      if (!cancelled) timer = setTimeout(tick, POLL_INTERVAL_MS);
    };
    void tick();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [refresh]);

  return (
    <section data-testid="chat-monitor">
      <h2>{t('in_client.host.chat.title', locale)}</h2>
      {error ? (
        <p role="alert" data-testid="chat-error">
          {t('in_client.error.network', locale)}: {error}
        </p>
      ) : null}
      {items.length === 0 ? (
        <p data-testid="chat-empty">{t('in_client.host.chat.empty', locale)}</p>
      ) : (
        <ul style={{ listStyle: 'none', padding: 0 }}>
          {items.map((cm) => (
            <ChatRow
              key={cm.id}
              ctx={ctx}
              locale={locale}
              chat={cm}
              onResolved={() => {
                setItems((prev) => prev.filter((x) => x.id !== cm.id));
              }}
              onError={(msg) => setError(msg)}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

interface ChatRowProps {
  ctx: AppContext;
  locale: Locale;
  chat: ChatMessage;
  onResolved: () => void;
  onError: (msg: string) => void;
}

function ChatRow({ ctx, locale, chat, onResolved, onError }: ChatRowProps): ReactElement {
  const [candidate, setCandidate] = useState<string>(chat.ai_response_candidate ?? '');
  const [generating, setGenerating] = useState(false);
  const [posting, setPosting] = useState(false);
  // host が候補をいじったかどうかを保持。投稿時に edited フラグとして送る。
  const [edited, setEdited] = useState(false);

  const handleSuggest = async () => {
    setGenerating(true);
    const r = await suggestChatReply(ctx, chat.id);
    setGenerating(false);
    if (r.ok) {
      setCandidate(r.candidate);
      setEdited(false);
    } else {
      onError(r.error);
    }
  };

  const handlePost = async () => {
    if (!candidate.trim()) return;
    setPosting(true);
    const r = await postChatReply(ctx, chat.id, candidate.trim(), edited);
    setPosting(false);
    if (r.ok) onResolved();
    else onError(r.error);
  };

  const handleIgnore = async () => {
    const r = await ignoreChatMessage(ctx, chat.id);
    if (r.ok) onResolved();
    else onError(r.error);
  };

  return (
    <li
      data-testid={`chat-item-${chat.id}`}
      style={{
        border: '1px solid #e5e7eb',
        borderRadius: 6,
        padding: 8,
        marginBottom: 8,
      }}
    >
      <div style={{ marginBottom: 4 }}>
        <strong>{chat.participant_name ?? 'Anonymous'}</strong>: {chat.content}
      </div>
      <div style={{ marginBottom: 4 }}>
        <button
          type="button"
          onClick={() => void handleSuggest()}
          disabled={generating}
          data-testid={`chat-suggest-${chat.id}`}
        >
          {generating
            ? t('in_client.host.chat.generating', locale)
            : t('in_client.host.chat.suggest', locale)}
        </button>
      </div>
      {candidate ? (
        <div>
          <small data-testid={`chat-ai-badge-${chat.id}`} style={{ opacity: 0.7 }}>
            [{t('in_client.ai.generated_label', locale)}]
          </small>
          <textarea
            value={candidate}
            onChange={(e) => {
              setCandidate(e.currentTarget.value);
              setEdited(true);
            }}
            data-testid={`chat-textarea-${chat.id}`}
            rows={4}
            style={{ width: '100%' }}
          />
        </div>
      ) : null}
      <div style={{ display: 'flex', gap: 8 }}>
        <button
          type="button"
          onClick={() => void handlePost()}
          disabled={!candidate.trim() || posting}
          data-testid={`chat-post-${chat.id}`}
        >
          {edited
            ? t('in_client.host.chat.post_edited', locale)
            : t('in_client.host.chat.post', locale)}
        </button>
        <button
          type="button"
          onClick={() => void handleIgnore()}
          data-testid={`chat-ignore-${chat.id}`}
        >
          {t('in_client.host.chat.ignore', locale)}
        </button>
      </div>
    </li>
  );
}
